import { useState, useEffect } from "react";
import { MasterDataServices } from "../services/master-data";
import { UserServices } from "../services/user";
import { MASTERDATA_TYPE } from "../constants/master-data";

type MasterDataType = typeof MASTERDATA_TYPE[keyof typeof MASTERDATA_TYPE];

interface UseMasterDataParams {
  types: MasterDataType[];
  includeUsers?: boolean;
  enabled?:boolean;
}

type MasterDataResult = Record<string, any[]> & { users?: any[] };

const typeToKeyMap: Record<MasterDataType, string> = {
  [MASTERDATA_TYPE.COUNTRY]: "countries",
  [MASTERDATA_TYPE.PAYMENT_METHOD]: "paymentMethods",
  [MASTERDATA_TYPE.CURRENCY]: "currencies",
  [MASTERDATA_TYPE.TERM_TYPE]: "termTypes",
  [MASTERDATA_TYPE.NUM_OF_INSTALLMENT]: "numOfInstallments",
  [MASTERDATA_TYPE.EVENT_TYPE]: "eventTypes",
  [MASTERDATA_TYPE.TIMING]: "timings",
  [MASTERDATA_TYPE.ABSOLUTE_DRIVER]: "absoluteDrivers",
  [MASTERDATA_TYPE.CALCULATION_BASIS]: "calculationBases",
  [MASTERDATA_TYPE.NOMINAL_ACCOUNT_DEBIT]: "nominalAccountDebits",
  [MASTERDATA_TYPE.NOMINAL_ACCOUNT_DEBIT_All]: "nominalAccountDebitsAll",
  [MASTERDATA_TYPE.NOMINAL_ACCOUNT_CREDIT]: "nominalAccountCredits",
  [MASTERDATA_TYPE.UNDERS_OVERS_AUTO_MATCH]: "underOverAutoMatches",
};

async function fetchMasterDataByType(type: MasterDataType) {
  const res = await MasterDataServices.getAllByType(type);
  return { key: typeToKeyMap[type], data: res?.data || [] };
}

async function fetchUsers() {
  const res = await UserServices.getBasic();
  return { key: "users", data: res?.data || [] };
}

export const useMasterData = ({ types, includeUsers = false, enabled = true }: UseMasterDataParams) => {
  const [data, setData] = useState<MasterDataResult>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const reload = () => setReloadCounter(prev => prev + 1);

  useEffect(() => {
    if (!enabled) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const promises = types.map(fetchMasterDataByType);

        if (includeUsers) {
          promises.push(fetchUsers());
        }

        const results = await Promise.all(promises);

        const resultObj = results.reduce((acc, curr) => {
          acc[curr.key] = curr.data;
          return acc;
        }, {} as MasterDataResult);

        setData(resultObj);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [types, includeUsers, reloadCounter, enabled]);

  return { data, loading, error, reload };
};
